import React, { useState, useEffect } from "react";
import EnquiryForm from "../../components/forms/enquiryForm";
import { motion, AnimatePresence } from "framer-motion";

const images = [
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/exterior.JPG",
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/interior.JPG",
  "https://arkbuild-cdn.s3.ap-southeast-2.amazonaws.com/images/22Ursulla/bathroom.JPG",
];

function ProjectDetailPage() {
  const [showForm, setShowForm] = useState(false);

  const [fullscreenImage, setFullscreenImage] = useState(null);

  useEffect(() => {
    document.body.style.overflow = showForm ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [showForm]);

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      {/* Main Content */}
      <div className="flex flex-col lg:flex-row h-full lg:overflow-y-scroll">
        <div className="w-full lg:w-1/2 h-auto lg:h-screen no-scrollbar scrollbar-hide z-10">
          {/* Mobile Layout */}
          <div className="block lg:hidden">
            {/* First image full width */}
            <img
              src={images[0]}
              alt="project 0"
              className="w-full object-cover py-2"
              onClick={() => setFullscreenImage(images[0])}
            />

            {/* Mobile: details and right side content will render after this */}
          </div>

          {/* Desktop layout (images scroll vertically) */}
          <div className="hidden lg:block">
            {images.map((src) => (
              <img
                src={src}
                alt={`Construction Image`}
                className="w-full object-cover py-2 cursor-pointer"
                onClick={() => setFullscreenImage(src)}
              />
            ))}
          </div>
        </div>

        {/* Right Side - Sticky Info */}
        <div className="w-full lg:w-1/2 h-auto lg:h-screen px-6 py-10 lg:px-16 lg:py-24 flex flex-col justify-between sticky top-0 bg-white text-[#111] z-20">
          <div className="space-y-10 pt-10">
            <a
              href="/featured-work"
              className="flex text-[#333] hover:underline font-medium"
            >
              Featured Work /
            </a>
            <div>
              <h1 className="text-4xl lg:text-5xl font-semibold pb-10">
                22 Ursula Street, Winston Hills
              </h1>
              <p className="text-sm text-[#444] leading-relaxed max-w-xl">
                22 Ursula is a bespoke residence that merges elegance and
                practicality. Every element of the design was carefully
                considered to maximise natural light, space, and comfort.
                Nestled in the heart of Winston Hills, this dual occupancy
                development reflects ARKBUILD’s commitment to thoughtful
                construction and seamless execution.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-6 text-sm">
              <div>
                <p className="font-semibold">Bedrooms</p>
                <p>5</p>
              </div>
              <div>
                <p className="font-semibold">Bathrooms</p>
                <p>3</p>
              </div>
              <div>
                <p className="font-semibold">Garage</p>
                <p>2</p>
              </div>
              <div>
                <p className="font-semibold">Status</p>
                <p>Under Construction</p>
              </div>
              <div>
                <p className="font-semibold">Type</p>
                <p>New Build</p>
              </div>
              <div>
                <a
                  href="/featured-work/1-Madonna-Street-Winston-Hills"
                  className="flex items-center gap-2 text-[#333] hover:underline pt-4 font-semibold"
                >
                  Next Project →
                </a>
              </div>
            </div>

            <div className="flex justify-center pb-10">
              <button
                onClick={() => setShowForm(true)}
                className="mt-6 bg-black text-white py-2 px-6 rounded hover:opacity-80 transition"
              >
                Enquire
              </button>
            </div>
          </div>

          <div className="block lg:hidden px-6 pt-6 pb-12">
            <div className="grid grid-cols-2 gap-4">
              <img
                src={images[1]}
                alt="project 1"
                className="w-full h-auto object-cover rounded"
                onClick={() => setFullscreenImage(images[1])}
              />
              <img
                src={images[2]}
                alt="project 2"
                className="w-full h-auto object-cover rounded"
                onClick={() => setFullscreenImage(images[2])}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="pt-10 border-t border-gray-200">
            <div className="flex justify-center">
              <div className="text-sm text-gray-600 mb-2">
                DESIGNED · DEVELOPED · BUILT BY
              </div>
            </div>
            <div className="flex justify-center">
              <div className="flex justify-start">
                <h3 className="text-2xl font-bold font-monserrat">
                  ARK<span className="font-medium">BUILD</span>
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slideout Form BELOW header */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="fixed top-0 bottom-0 right-0 w-full lg:w-1/2 h-full z-40 bg-black"
          >
            <EnquiryForm onClose={() => setShowForm(false)} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setFullscreenImage(null)}
          >
            <motion.img
              src={fullscreenImage}
              alt="Fullscreen"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking the image
            />
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-6 right-6 text-white text-2xl"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ProjectDetailPage;
